import { CartItem } from "@/redux/slice/cart/types/CartItem";
import { ServerCartItems } from "@/redux/slice/cart/types/ServerCartItems";
import {
  GetGuestCartDetails_getGuestCartDetails_groupCards_products as GuestCartProduct,
  GetGuestCartDetails_getGuestCartDetails_groupCards as GuestCartGroupCard,
} from "@/components/graphql/types/FetchGuestCart";
import { BmsmTierData } from "../types/BmsmTierData";
import { CorporateStatus, Shipment } from "@/components/graphql/types/globalTypes";

export const getPatientCartItemsFromServer = (serverCartItems: ServerCartItems = []): CartItem[] => {
  const cartItems: CartItem[] = [];

  if (serverCartItems?.length) {
    serverCartItems
      .map(
        ({
          sku,
          magentoId,
          magentoStatus,
          name,
          quantity,
          price,
          sellingPrice,
          mou,
          thumbnail,
          isVCRestricted,
          isPrescriptionRequired,
          subcategory,
          typeId,
          urlKey,
          isInStock,
          maxOrderQty,
          sellOnline,
          isCouponApplicable,
          isShippable,
          cashback,
          freeProduct,
          tatCity,
          tatDuration,
          storeType,
          couponDiscountPrice,
          itemCouponCashback,
          setReminder,
          reminderDuration,
          packForm,
          packSize,
          isVariantAvailable,
          multiVariants,
          moq,
          corporateStatus,
        }) => {
          return {
            url_key: urlKey,
            description: "",
            id: magentoId,
            image: [],
            isVCRestricted,
            is_in_stock: Boolean(isInStock),
            sell_online: Boolean(sellOnline),
            is_prescription_required: isPrescriptionRequired,
            name,
            price,
            sellingPrice,
            sku,
            small_image: thumbnail,
            status: Number(magentoStatus),
            thumbnail,
            type_id: typeId,
            quantity,
            mou,
            isShippable,
            MaxOrderQty: maxOrderQty,
            couponFree: Number(freeProduct),
            subcategory,
            cashback,
            isCouponApplicable,
            tatCity,
            tatDuration,
            storeType,
            couponDiscountPrice,
            itemCouponCashback,
            packForm,
            packSize,
            isReminderSet: setReminder,
            reminderDuration,
            isVariantAvailable,
            multiVariants,
            oosNotified: false,
            moq,
            corporateStatus,
          } as CartItem;
        }
      )
      .forEach((cartItem) => {
        cartItems.push(cartItem);
      });
  }

  return cartItems;
};

export const getGuestCartItemsFromServer = (
  serverCartItems: GuestCartProduct[] = []
): CartItem[] => {
  if (!serverCartItems?.length) return [];
  return (
    serverCartItems?.map((item) => {
      const {
        sku,
        discountInfo,
        qtyDropdown,
        flags,
        corporate,
        options,
        bmsmDetails,
      } = item;

      const selectedQty =
        qtyDropdown?.quantities?.find((q) => q.isSelected)?.qty || 1;
      const bmsmTiersData: BmsmTierData[] = (bmsmDetails?.tiers || [])
        .filter((tier): tier is NonNullable<typeof tier> => tier !== null)
        .map((tier) => ({
          tier: tier.precedence ?? null,
          quantity: tier.quantity ?? null,
          discount: tier.discountInPercent ?? null,
          price: null,
        }));

      return {
        id: sku?.id ?? 0,
        sku: sku?.sku ?? "",
        name: sku?.name ?? "",
        description: "",
        url_key: sku?.urlKey ?? "",
        subcategory: sku?.subcategory || "",
        type_id: sku?.type || "",
        mou: sku?.packInformation ?? "",
        moq: sku?.moq ?? 1,
        price: discountInfo?.mrp ?? 0,
        sellingPrice: discountInfo?.price ?? 0,
        special_price: discountInfo?.price ?? 0,
        discountPercentage: discountInfo?.discountInPercent ?? null,
        discount:
          discountInfo?.mrp && discountInfo?.price
            ? discountInfo.mrp - discountInfo.price
            : null,
        originalPrice: discountInfo?.mrp ?? null,
        quantity: selectedQty,
        MaxOrderQty: 10,
        isVCRestricted: sku?.isVCRestricted ?? false,
        is_prescription_required: sku?.isPrescriptionRequired ? "1" : "0",
        status: "in-stock",
        thumbnail: sku?.thumbnail ?? null,
        small_image: sku?.thumbnail ?? null,
        image: null,
        couponFree: flags?.freebie ? 1 : 0,
        cashback: null,
        circleCashbackObject: null,
        circleCashback: discountInfo?.circleCashBackDetails ?? null,
        isCouponApplicable: null,
        couponDiscountPrice: null,
        itemCouponCashback: null,
        corporateStatus: (corporate?.status as CorporateStatus) ?? null,
        tatCity: null,
        tatDuration: null,
        storeType: null,
        isReminderSet: false,
        reminderDuration: null,
        is_express: null,
        brand: sku?.manufacturer ?? null,
        isVariantAvailable: options?.isMultiVariants ?? false,
        multiVariants: null,
        isSubstituteAvailable: options?.isSubstitute ?? false,
        susbtitutes: null,
        isReplacementProductsAvailable: options?.isReplacementProducts ?? false,
        replacementProducts: null,
        oosNotified: false,
        transaction: null,
        bmsm_tiers_data: bmsmTiersData,
        isShippable: false,
      } as CartItem;
    }) || []
  );
};
export const getGuestCartShipments = (
  groupCards: (GuestCartGroupCard | null)[] = [],
  noOfShipments: number = 1
): Shipment[] => {
  const outOfStockShipments: Shipment[] = [],
    inStockShipments: Shipment[] = [];

  for (
    let shipmentNumber = 1;
    shipmentNumber <= noOfShipments;
    shipmentNumber++
  ) {
    const shipmentGroupCard = groupCards?.find(
      (groupCard) => groupCard?.shipmentDetails?.shipmentNo === shipmentNumber
    );

    if (shipmentGroupCard?.products) {
      const shipmentProducts = shipmentGroupCard.products.filter(
        (product): product is GuestCartProduct => product !== null
      );
      const shipmentCartItems = getGuestCartItemsFromServer(shipmentProducts);

      if (shipmentCartItems?.length > 0) {
        const isOutOfStockShipment =
          shipmentGroupCard.shipmentDetails?.isShippable === false ||
          !!shipmentCartItems.find(({ isShippable }) => !isShippable);

        const tat =
          shipmentGroupCard.shipmentDetails?.tat?.toString() || undefined;

        if (isOutOfStockShipment) {
          outOfStockShipments.push({
            items: shipmentCartItems,
            orderTat: tat,
            estimatedAmount: shipmentCartItems.reduce(
              (acc, curr) => acc + curr.sellingPrice,
              0
            ),
            isOutOfStockShipment: true,
            shipmentNumber,
          });
        } else {
          inStockShipments.push({
            items: shipmentCartItems,
            orderTat: tat,
            estimatedAmount: shipmentCartItems.reduce(
              (acc, curr) => acc + curr.sellingPrice,
              0
            ),
            isOutOfStockShipment: false,
            shipmentNumber,
          });
        }
      }
    }
  }

  return inStockShipments.concat(outOfStockShipments);
};
